<template>
  <div class="min-h-screen mt-[75px]">
    <div
      class="sticky z-999 top-0 z-10 backdrop-blur-md bg-white/80 border-b border-white/20 shadow-lg"
    >
      <div class="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-6">
        <div class="flex items-center justify-between flex-wrap gap-3">
          <div class="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
            <button
              @click="goToExercices"
              class="group cursor-pointer relative overflow-hidden bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white p-2 sm:p-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform flex-shrink-0"
            >
              <div
                class="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
              ></div>
              <i class="fa-solid fa-arrow-left relative z-10 text-sm sm:text-base"></i>
            </button>
            <div class="min-w-0 flex-1">
              <h1
                class="text-lg sm:text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent truncate"
              >
                Résultats de l'exercice
              </h1>
              <p class="text-sm sm:text-base text-gray-600 font-medium truncate">
                {{ attemptData?.exercice?.title }}
              </p>
            </div>
          </div>

          <div class="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            <button
              @click="retryExercice"
              class="group cursor-pointer relative overflow-hidden bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold py-2 px-3 sm:py-3 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform text-sm sm:text-base"
            >
              <div
                class="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
              ></div>
              <i class="fa-solid fa-redo mr-1 sm:mr-2 relative z-10 text-sm"></i>
              <span class="relative z-10 hidden sm:inline">Refaire l'exercice</span>
              <span class="relative z-10 sm:hidden">Refaire</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-32">
      <LoadingState></LoadingState>
    </div>

    <div v-else-if="error" class="max-w-4xl mx-auto px-6 py-16">
      <div
        class="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-2xl p-10 text-center shadow-lg"
      >
        <div
          class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <i class="fa-solid fa-exclamation-triangle text-red-500 text-2xl"></i>
        </div>
        <h3 class="text-xl font-bold text-red-700 mb-3">Oups ! Une erreur s'est produite</h3>
        <p class="text-red-600 mb-6">{{ error }}</p>
        <button
          @click="loadResults"
          class="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform"
        >
          Réessayer
        </button>
      </div>
    </div>

    <div v-else class="max-w-7xl mx-auto lg:px-6 lg:py-8 pt-8 space-y-8">
      <!-- Correction en cours pour les exercices d'expression orale -->
      <div v-if="isWaitingForCorrection" class="max-w-4xl mx-auto px-6 py-16">
        <div
          class="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-10 text-center shadow-lg"
        >
          <div
            class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <i class="fa-solid fa-microphone-lines text-blue-500 text-2xl animate-pulse"></i>
          </div>

          <h3 class="text-2xl font-bold text-blue-700 mb-4">Correction en cours...</h3>
          <p class="text-blue-600 mb-6">Veuillez patienter.</p>

          <!-- Barre de progression basée sur les exercices -->
          <div class="w-full max-w-md mx-auto mb-6">
            <div class="flex justify-end text-sm text-blue-600 mb-2">
              <span><i class="fa-solid fa-spinner fa-spin"></i></span>
            </div>
            <div class="w-full bg-blue-200 rounded-full h-4">
              <div
                class="bg-gradient-to-r from-blue-500 to-indigo-600 h-4 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                :style="{ width: correctionProgress + '%' }"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"
                ></div>
              </div>
            </div>
          </div>

          <div class="text-sm text-blue-500">
            <i class="fa-solid fa-info-circle mr-1"></i>
            La correction peut prendre quelques minutes selon la complexité de vos réponses
          </div>
        </div>
      </div>

      <!-- État de timeout -->
      <!-- <div v-else-if="correctionTimeout" class="max-w-4xl mx-auto px-6 py-16">
        <div
          class="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-2xl p-10 text-center shadow-lg"
        >
          <div
            class="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <i class="fa-solid fa-clock text-orange-500 text-2xl"></i>
          </div>
          <h3 class="text-xl font-bold text-orange-700 mb-3">Correction en cours de traitement</h3>
          <p class="text-orange-600 mb-6">
            La correction prend plus de temps que prévu. Cela peut arriver lors de pics d'activité.
          </p>
          <div class="space-y-3">
            <button
              @click="checkCorrectionStatus"
              class="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform mr-4"
            >
              <i class="fa-solid fa-refresh mr-2"></i>
              Vérifier le statut
            </button>
            <button
              @click="goToExercices"
              class="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform"
            >
              Retour aux exercices
            </button>
          </div>
          <p class="text-sm text-orange-500 mt-4">
            Vous pouvez revenir plus tard pour consulter vos résultats.
          </p>
        </div>
      </div> -->
      <div v-else>
        <div
          class="relative mb-8 overflow-hidden rounded-3xl p-3 lg:p-8 shadow-lg border border-gray-200"
        >
          <div
            class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100/50 to-transparent rounded-full transform translate-x-16 -translate-y-16"
          ></div>
          <div
            class="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-100/50 to-transparent rounded-full transform -translate-x-12 translate-y-12"
          ></div>

          <div class="relative z-10 text-center mb-8">
            <div class="inline-block">
              <div class="text-gray-700 text-lg mb-2">Votre score final</div>
              <div class="relative">
                <div class="text-6xl font-bold text-primary-500">
                  {{ finalScore }} <span class="text-gray-500 text-sm">/ 699</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Statistiques avec cards responsive -->
          <div
            class="flex flex-col gap-4 sm:gap-6 justify-center items-center mb-6 sm:mb-8 sm:flex-row"
          >
            <div
              v-if="['LISTENING', 'READING'].includes(ExerciceType)"
              class="w-full sm:w-1/3 group rounded-xl sm:rounded-2xl py-3 sm:py-2 text-center transform transition-all duration-300 border border-gray-200"
            >
              <div
                class="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:rotate-12 transition-transform duration-300"
              >
                <i class="fa-solid fa-check text-white text-sm sm:text-base"></i>
              </div>
              <div class="text-xl sm:text-2xl font-bold text-green-600 mb-2">
                {{ correctAnswers }}
              </div>
              <p class="text-sm sm:text-base text-green-700 font-medium">Bonnes réponses</p>
            </div>

            <div
              v-if="['LISTENING', 'READING'].includes(ExerciceType)"
              class="w-full sm:w-1/3 group rounded-xl sm:rounded-2xl py-3 sm:py-2 text-center transform transition-all duration-300 border border-gray-200"
            >
              <div
                class="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-red-400 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:rotate-12 transition-transform duration-300"
              >
                <i class="fa-solid fa-times text-white text-sm sm:text-base"></i>
              </div>
              <div class="text-xl sm:text-2xl font-bold text-red-600 mb-2">
                {{ incorrectAnswers }}
              </div>
              <p class="text-sm sm:text-base text-red-700 font-medium">Mauvaises réponses</p>
            </div>

            <div
              v-if="['SPEAKING', 'WRITING'].includes(ExerciceType)"
              class="w-full sm:w-1/3 group rounded-xl sm:rounded-2xl py-3 sm:py-2 text-center transform transition-all duration-300 border border-gray-200"
            >
              <div
                class="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-primary-400 to-primary-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:rotate-12 transition-transform duration-300"
              >
                <i class="fa-solid fa-check text-white text-sm sm:text-base"></i>
              </div>
              <div class="text-xl sm:text-2xl font-bold text-primary-600 mb-2">
                {{ nbrAnswersText }}
              </div>
              <p class="text-sm sm:text-base text-primary-700 font-medium">Réponses</p>
            </div>

            <div
              class="w-full sm:w-1/3 group rounded-xl sm:rounded-2xl py-3 sm:py-2 text-center transform transition-all duration-300 border border-gray-200"
            >
              <div
                class="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:rotate-12 transition-transform duration-300"
              >
                <i class="fa-solid fa-clock text-white text-sm sm:text-base"></i>
              </div>
              <div class="text-xl sm:text-2xl font-bold text-blue-600 mb-2">
                {{ formatTime(timeSpent) }}
              </div>
              <p class="text-sm sm:text-base text-blue-700 font-medium">Temps passé</p>
            </div>
          </div>

          <!-- Niveau CECRL avec design moderne -->
          <div class="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-white/50">
            <h3 class="text-xl font-bold text-gray-800 mb-6 text-center">
              Votre Niveau Linguistique CECRL
              <span v-if="isLevelAttained" class="block mt-2">
                <span
                  class="inline-block bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-full text-lg font-bold"
                >
                  {{ attainedLevel }}
                </span>
              </span>
            </h3>

            <!-- Indicateur de niveau modernisé -->
            <div class="relative w-full max-w-2xl mx-auto mb-6">
              <div
                class="flex items-end justify-between bg-gradient-to-r from-gray-200 to-gray-300 rounded-full h-8 p-1"
              >
                <template v-for="(lvl, index) in levels" :key="lvl">
                  <div class="flex flex-col items-center justify-end h-full flex-1">
                    <div class="text-xs font-bold text-gray-600 mb-1">{{ lvl }}</div>
                    <div
                      class="w-full h-3 rounded-full transition-all duration-500"
                      :class="
                        ['A1', 'B1', 'C1'].includes(lvl)
                          ? 'bg-gradient-to-t from-blue-400 to-blue-600'
                          : 'bg-gradient-to-t from-gray-300 to-gray-400'
                      "
                    ></div>
                  </div>
                </template>
                <div
                  class="absolute top-1 transform -translate-x-1/2 transition-all duration-700 ease-out"
                  :style="{ left: markerPosition }"
                >
                  <div
                    class="w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full shadow-lg animate-pulse"
                  ></div>
                </div>
              </div>
            </div>

            <p class="text-center text-red-500 font-bold" v-if="!isLevelAttained">
              <i class="fa-solid fa-exclamation-circle mr-2"></i>
              {{ currentLevel }} Non atteint
            </p>
          </div>

          <!-- Tableau NCLC modernisé -->
          <div class="mt-8">
            <div
              class="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden shadow-xl"
            >
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead>
                    <tr class="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                      <th class="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">
                        NCLC
                      </th>
                      <th class="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">
                        Compréhension orale
                      </th>
                      <th class="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">
                        Compréhension écrite
                      </th>
                      <th class="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">
                        Expression orale
                      </th>
                      <th class="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">
                        Expression écrite
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr class="bg-blue-50 hover:bg-blue-100 transition-colors duration-200">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-800">
                        10 et plus
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        549 à 699 <span class="font-semibold text-blue-700">(C1-C2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        549 à 699 <span class="font-semibold text-blue-700">(C1-C2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        16 à 20 <span class="font-semibold text-blue-700">(C1-C2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        16 à 20 <span class="font-semibold text-blue-700">(C1-C2)</span>
                      </td>
                    </tr>

                    <tr class="bg-white hover:bg-blue-100 transition-colors duration-200">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-800">
                        9
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        523 à 548 <span class="font-semibold text-blue-700">(C1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        524 à 548 <span class="font-semibold text-blue-700">(C1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        14-15 <span class="font-semibold text-blue-700">(C1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        14-15 <span class="font-semibold text-blue-700">(C1)</span>
                      </td>
                    </tr>

                    <tr class="bg-blue-50 hover:bg-blue-100 transition-colors duration-200">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-800">
                        8
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        503 à 522 <span class="font-semibold text-blue-700">(B2-C1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        499 à 523 <span class="font-semibold text-blue-700">(B2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        12-13 <span class="font-semibold text-blue-700">(B2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        12-13 <span class="font-semibold text-blue-700">(B2)</span>
                      </td>
                    </tr>

                    <tr class="bg-white hover:bg-blue-100 transition-colors duration-200">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-800">
                        7
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        458 à 502 <span class="font-semibold text-blue-700">(B2-C1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        453 à 498 <span class="font-semibold text-blue-700">(B2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        10-11 <span class="font-semibold text-blue-700">(B2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        10-11 <span class="font-semibold text-blue-700">(B2)</span>
                      </td>
                    </tr>

                    <tr class="bg-blue-50 hover:bg-blue-100 transition-colors duration-200">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-800">
                        6
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        398 à 457 <span class="font-semibold text-blue-700">(B1-B2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        406 à 452 <span class="font-semibold text-blue-700">(B2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        7-8-9 <span class="font-semibold text-blue-700">(B1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        7-8-9 <span class="font-semibold text-blue-700">(B1)</span>
                      </td>
                    </tr>

                    <tr class="bg-white hover:bg-blue-100 transition-colors duration-200">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-800">
                        5
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        369 à 397 <span class="font-semibold text-blue-700">(B1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        375 à 405 <span class="font-semibold text-blue-700">(B1-B2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        6 <span class="font-semibold text-blue-700">(B1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        6 <span class="font-semibold text-blue-700">(B1)</span>
                      </td>
                    </tr>

                    <tr class="bg-blue-50 hover:bg-blue-100 transition-colors duration-200">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-800">
                        4
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        331 à 368 <span class="font-semibold text-blue-700">(B1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        342 à 374 <span class="font-semibold text-blue-700">(B1)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        4-5 <span class="font-semibold text-blue-700">(A2)</span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        4-5 <span class="font-semibold text-blue-700">(A2)</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Détail des réponses avec cards modernes -->
        <div
          v-if="['LISTENING', 'READING'].includes(ExerciceType)"
          class="bg-white/90 backdrop-blur-sm border-2 border-white/50 rounded-3xl p-8 shadow-2xl"
        >
          <div class="flex items-center justify-between mb-8">
            <h3
              class="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent"
            >
              <i class="fa-solid fa-list-check text-blue-500 mr-3"></i>
              Détail des réponses
            </h3>
            <div class="flex items-center space-x-3">
              <button
                @click="filterCorrect = !filterCorrect"
                class="group px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 transform"
                :class="
                  filterCorrect
                    ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                "
              >
                <i class="fa-solid fa-check mr-2 group-hover:animate-pulse"></i>
                Correctes
              </button>
              <button
                @click="filterIncorrect = !filterIncorrect"
                class="group px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 transform"
                :class="
                  filterIncorrect
                    ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                "
              >
                <i class="fa-solid fa-times mr-2 group-hover:animate-pulse"></i>
                Incorrectes
              </button>
            </div>
          </div>

          <!-- Grid des réponses avec hover effects -->
          <div class="grid gap-6 lg:grid-cols-3 md:grid-cols-2 grid-cols-1">
            <div
              v-for="(answer, index) in filteredAnswers"
              :key="answer.questionSlug"
              class="group relative overflow-hidden border-2 rounded-2xl p-6 cursor-pointer transition-all duration-300 transform hover:shadow-2xl"
              :class="
                answerIsCorrect(answer)
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 hover:border-green-300'
                  : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200 hover:border-red-300'
              "
              @click="resultIsHovered = index"
            >
              <!-- Effet de brillance au hover -->
              <div
                class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
              ></div>

              <div class="relative z-10">
                <div class="flex items-center justify-between mb-4">
                  <div class="flex items-center space-x-3">
                    <div class="relative">
                      <div
                        class="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                        :class="
                          answerIsCorrect(answer)
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                            : 'bg-gradient-to-r from-red-500 to-rose-600 text-white'
                        "
                      >
                        <i
                          :class="
                            answerIsCorrect(answer) ? 'fa-solid fa-check' : 'fa-solid fa-times'
                          "
                          class="text-lg"
                        ></i>
                      </div>
                      <div
                        class="absolute -inset-1 rounded-full opacity-50 group-hover:animate-ping"
                        :class="answerIsCorrect(answer) ? 'bg-green-400' : 'bg-red-400'"
                      ></div>
                    </div>
                    <div>
                      <h4 class="font-bold text-gray-900 text-lg">
                        Question {{ index + 1
                        }}<small class="text-gray-500 font-medium text-xs"
                          >/{{ answer.points }} points</small
                        >
                      </h4>
                    </div>
                  </div>
                  <div class="text-right">
                    <span
                      class="inline-block px-3 py-1 text-sm font-bold rounded-full"
                      :class="
                        answerIsCorrect(answer)
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      "
                    >
                      {{ answerIsCorrect(answer) ? 'Correcte' : 'Incorrecte' }}
                    </span>
                  </div>
                </div>

                <!-- Contenu détaillé avec animation -->
                <div v-if="resultIsHovered == index" v-auto-animate class="space-y-4">
                  <div v-for="(ressource, index) in answer.ressouces" :key="index" class="w-full">
                    <img
                      class="w-full rounded-xl border-2 border-gray-200 shadow-lg"
                      v-if="ressource.image_file"
                      :src="ressource.image_file"
                      alt=""
                    />
                    <audio
                      v-if="ressource.audio_file"
                      controls
                      class="w-full mt-3 rounded-lg"
                      :key="ressource.audio_file"
                    >
                      <source :src="ressource.audio_file" type="audio/mpeg" />
                      Votre navigateur ne prend pas en charge l'élément audio.
                    </audio>
                  </div>

                  <div
                    class="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 space-y-3"
                  >
                    <div
                      v-for="(choice, idxChoice) in answer?.choices"
                      :key="idxChoice"
                      class="py-3 px-4 text-sm font-medium border-2 rounded-xl transition-all duration-300"
                      :class="getChoiceColor(answer, idxChoice)"
                    >
                      {{ choice?.choice_text }}
                    </div>
                  </div>
                </div>

                <!-- Réponses -->
                <div class="mt-4 space-y-2 text-sm">
                  <div class="flex items-start space-x-2">
                    <span class="font-bold text-gray-700">Votre réponse:</span>
                    <span
                      class="font-medium"
                      :class="answerIsCorrect(answer) ? 'text-green-700' : 'text-red-700'"
                    >
                      {{ answer.user_choice[0]?.choice_text }}
                    </span>
                  </div>
                  <div v-if="!answerIsCorrect(answer)" class="flex items-start space-x-2">
                    <span class="font-bold text-gray-700">Bonne réponse:</span>
                    <span class="text-green-700 font-bold">
                      {{ answer.choices.find((c) => c.is_correct == 1)?.choice_text }}
                    </span>
                  </div>
                </div>
              </div>

              <div
                class="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                <span
                  class="text-xs bg-black/20 backdrop-blur-sm text-white px-2 py-1 rounded-full"
                >
                  Cliquez pour voir les détails
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="bg-white/90 backdrop-blur-sm border-2 border-white/50 rounded-3xl py-3 lg:p-8 shadow-2xl"
        >
          <div class="flex items-center justify-between mb-8">
            <h3
              class="text-2xl px-3 mt-2 font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent"
            >
              <i class="fa-solid fa-list-check text-blue-500 mr-3"></i>
              Réponses
            </h3>
          </div>

          <!-- Grid des réponses avec hover effects -->
          <div class="grid gap-6 grid-cols-1">
            <div
              v-for="(answer, index) in filteredAnswers"
              :key="answer.questionSlug"
              class="group bg-primary-50/50 border-primary-50 relative overflow-hidden border-2 rounded-2xl p-3 transition-all duration-300 transform"
              @click="resultIsHovered = index"
            >
              <!-- Effet de brillance au hover -->
              <div
                class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
              ></div>

              <div class="relative z-10">
                <div class="flex items-center justify-between mb-4">
                  <div class="flex items-center space-x-3">
                    <div>
                      <h4 class="font-bold text-gray-900 text-lg">Question {{ index + 1 }}</h4>
                    </div>
                  </div>
                </div>

                <!-- Contenu détaillé avec animation -->

                <!-- Réponses -->
                <div class="mt-4 space-y-2 text-sm">
                  <div class="flex flex-col items-start space-x-2">
                    <span class="font-bold text-gray-700">Sujet</span>
                    <p class="bg-white/60 p-2 rounded-lg mt-1 border-2 border-primary-50">
                      {{ getQuestionTitle(answer?.question_text) }}
                    </p>
                    <span class="font-bold mt-5 text-gray-700">Votre réponse</span>
                    <p
                      class="w-full py-5 font-medium text-center text-gray-500"
                      v-if="!answer?.user_aswer_text && answer?.ai_aswer"
                    >
                      Aucune réponse
                    </p>
                    <div
                      v-if="!answer?.ai_aswer"
                      class="py-5 flex justify-center items-center w-full"
                    >
                      <button
                        class="px-5 py-3 bg-primary-500 text-white rounded-lg"
                        @click="correctAnswer(answer)"
                      >
                        <i
                          class="fas fa-refresh mr-2"
                          :class="{ 'fa-spin': isCorrectingAnsawer(answer.slug) }"
                        ></i>
                        <span v-if="!isCorrectingAnsawer(answer.slug)">Corriger cette reponse</span>
                        <span v-else>Correction ...</span>
                      </button>
                    </div>
                    <p
                      class="bg-white p-2 rounded-lg mt-1"
                      v-if="answer?.user_aswer_text && ExerciceType !== 'SPEAKING'"
                    >
                      {{ answer?.user_aswer_text }}
                    </p>
                    <p v-if="answer?.user_aswer_audio">
                      <audio-player :src="answer?.user_aswer_audio"></audio-player>
                    </p>
                    <div v-if="answer?.ai_aswer" class="mt-6 space-y-6">
                      <!-- Audio Analysis Section (for SPEAKING exercises) -->
                      <div
                        v-if="ExerciceType === 'SPEAKING' && answer.ai_aswer.audio_analyse"
                        class="bg-white rounded-xl border-2 border-indigo-100 shadow-sm"
                      >
                        <div
                          class="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-t-xl border-b border-indigo-100"
                        >
                          <h4 class="font-bold text-indigo-800 text-lg flex items-center">
                            <i class="fa-solid fa-microphone text-indigo-500 mr-2"></i>
                            Analyse audio
                          </h4>
                        </div>

                        <div class="p-4 space-y-4">
                          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div
                              v-if="answer.ai_aswer.audio_analyse?.purete_voix"
                              class="bg-indigo-50 border border-indigo-200 rounded-lg p-4"
                            >
                              <h5 class="font-semibold text-indigo-700 mb-2 flex items-center">
                                <i class="fa-solid fa-volume-high text-indigo-500 mr-2 text-sm"></i>
                                Pureté de la voix
                              </h5>
                              <p class="text-sm text-indigo-700">
                                {{ answer.ai_aswer.audio_analyse.purete_voix }}
                              </p>
                            </div>

                            <div
                              v-if="answer.ai_aswer.audio_analyse.instabilite_voix"
                              class="bg-indigo-50 border border-indigo-200 rounded-lg p-4"
                            >
                              <h5 class="font-semibold text-indigo-700 mb-2 flex items-center">
                                <i class="fa-solid fa-wave-square text-indigo-500 mr-2 text-sm"></i>
                                Stabilité vocale
                              </h5>
                              <p class="text-sm text-indigo-700">
                                {{ answer.ai_aswer.audio_analyse.instabilite_voix }}
                              </p>
                            </div>

                            <div
                              v-if="answer.ai_aswer.audio_analyse.instabilite_volume"
                              class="bg-indigo-50 border border-indigo-200 rounded-lg p-4"
                            >
                              <h5 class="font-semibold text-indigo-700 mb-2 flex items-center">
                                <i class="fa-solid fa-sliders text-indigo-500 mr-2 text-sm"></i>
                                Stabilité du volume
                              </h5>
                              <p class="text-sm text-indigo-700">
                                {{ answer.ai_aswer.audio_analyse.instabilite_volume }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Score Estimation -->
                      <div
                        v-if="answer.ai_aswer.analyse_tcf?.note_estimee_20 != null"
                        class="bg-white rounded-xl border-2 border-gray-100 p-6 shadow-sm"
                      >
                        <div class="flex items-center justify-between mb-4">
                          <h4 class="font-bold text-gray-800 text-lg flex items-center">
                            <i class="fa-solid fa-chart-line text-blue-500 mr-2"></i>
                            Note estimée
                          </h4>
                          <div
                            class="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-full font-bold text-lg"
                          >
                            {{ answer.ai_aswer.analyse_tcf.note_estimee_20 }} / 20
                          </div>
                        </div>

                        <!-- TCF Analysis Grid -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div class="bg-gray-50 rounded-lg p-4">
                            <h5 class="font-semibold text-gray-700 mb-2 flex items-center">
                              <i class="fa-solid fa-puzzle-piece text-purple-500 mr-2 text-sm"></i>
                              Cohérence
                            </h5>
                            <p class="text-sm text-gray-600">
                              {{ answer.ai_aswer.analyse_tcf.coherence }}
                            </p>
                          </div>

                          <div class="bg-gray-50 rounded-lg p-4">
                            <h5 class="font-semibold text-gray-700 mb-2 flex items-center">
                              <i class="fa-solid fa-bullseye text-green-500 mr-2 text-sm"></i>
                              Pertinence
                            </h5>
                            <p class="text-sm text-gray-600">
                              {{ answer.ai_aswer.analyse_tcf.pertinence }}
                            </p>
                          </div>

                          <div class="bg-gray-50 rounded-lg p-4">
                            <h5 class="font-semibold text-gray-700 mb-2 flex items-center">
                              <i class="fa-solid fa-book text-blue-500 mr-2 text-sm"></i>
                              Richesse vocabulaire
                            </h5>
                            <p class="text-sm text-gray-600">
                              {{ answer.ai_aswer.analyse_tcf.richesse_vocabulaire }}
                            </p>
                          </div>

                          <div class="bg-gray-50 rounded-lg p-4">
                            <h5 class="font-semibold text-gray-700 mb-2 flex items-center">
                              <i class="fa-solid fa-spell-check text-orange-500 mr-2 text-sm"></i>
                              Correction grammaticale
                            </h5>
                            <p class="text-sm text-gray-600">
                              {{ answer.ai_aswer.analyse_tcf.correction_grammaticale }}
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- Corrections Section -->
                      <div
                        v-if="answer.ai_aswer.erreurs && answer.ai_aswer.erreurs.length > 0"
                        class="bg-white rounded-xl border-2 border-red-100 shadow-sm"
                      >
                        <div
                          class="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-t-xl border-b border-red-100"
                        >
                          <h4 class="font-bold text-red-800 text-lg flex items-center">
                            <i class="fa-solid fa-exclamation-triangle text-red-500 mr-2"></i>
                            Erreurs identifiées ({{ answer.ai_aswer.erreurs.length }})
                          </h4>
                        </div>

                        <div class="p-4 space-y-4">
                          <div
                            v-for="(erreur, idx) in answer.ai_aswer.erreurs"
                            :key="idx"
                            class="relative"
                          >
                            <div
                              class="flex items-start space-x-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg"
                            >
                              <!-- <div
                                class="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center"
                              >
                                <span class="text-red-600 font-bold text-sm">{{ idx + 1 }}</span>
                              </div> -->

                              <div class="flex-1 space-y-3">
                                <!-- Original vs Correction -->
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div class="bg-white rounded-lg p-3 border border-red-200">
                                    <label
                                      class="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1 block"
                                    >
                                      Texte original
                                    </label>
                                    <p class="text-red-800 font-mono bg-red-50 px-2 py-1 rounded">
                                      "{{ erreur.phrase_originale }}"
                                    </p>
                                  </div>

                                  <div class="bg-white rounded-lg p-3 border border-green-200">
                                    <label
                                      class="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1 block"
                                    >
                                      Correction
                                    </label>
                                    <p
                                      class="text-green-800 font-mono bg-green-50 px-2 py-1 rounded"
                                    >
                                      "{{ erreur.correction }}"
                                    </p>
                                  </div>
                                </div>

                                <!-- Explanation -->
                                <div class="bg-white rounded-lg p-3 border border-gray-200">
                                  <label
                                    class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1 block"
                                  >
                                    Explication
                                  </label>
                                  <p class="text-gray-700 leading-relaxed">
                                    {{ erreur.explication }}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Corrected Text -->
                      <div
                        v-if="answer.ai_aswer.texte_corrige"
                        class="bg-white rounded-xl border-2 border-green-100 shadow-sm"
                      >
                        <div
                          class="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-t-xl border-b border-green-100"
                        >
                          <h4 class="font-bold text-green-800 text-lg flex items-center">
                            <i class="fa-solid fa-check-circle text-green-500 mr-2"></i>
                            Texte corrigé
                          </h4>
                        </div>

                        <div class="p-6">
                          <div class="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
                            <p
                              class="text-gray-800 leading-relaxed whitespace-pre-line font-medium"
                            >
                              {{ answer.ai_aswer.texte_corrige }}
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- Subject Relevance Analysis -->
                      <div
                        v-if="answer.ai_aswer.pertinence_sujet"
                        class="bg-white rounded-xl border-2 border-blue-100 shadow-sm"
                      >
                        <div
                          class="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-t-xl border-b border-blue-100"
                        >
                          <h4 class="font-bold text-blue-800 text-lg flex items-center">
                            <i class="fa-solid fa-target text-blue-500 mr-2"></i>
                            Analyse de la pertinence
                          </h4>
                        </div>

                        <div class="p-4 space-y-4">
                          <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <!-- Points forts -->
                            <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                              <h5 class="font-semibold text-green-700 mb-2 flex items-center">
                                <i class="fa-solid fa-thumbs-up text-green-500 mr-2 text-sm"></i>
                                Points forts
                              </h5>
                              <p class="text-sm text-green-700">
                                {{ answer.ai_aswer.pertinence_sujet.points_forts }}
                              </p>
                            </div>

                            <!-- Respect de la consigne -->
                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h5 class="font-semibold text-blue-700 mb-2 flex items-center">
                                <i
                                  class="fa-solid fa-clipboard-check text-blue-500 mr-2 text-sm"
                                ></i>
                                Respect consigne
                              </h5>
                              <p class="text-sm text-blue-700">
                                {{ answer.ai_aswer.pertinence_sujet.respect_consigne }}
                              </p>
                            </div>

                            <!-- Points à améliorer -->
                            <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
                              <h5 class="font-semibold text-orange-700 mb-2 flex items-center">
                                <i
                                  class="fa-solid fa-arrow-trend-up text-orange-500 mr-2 text-sm"
                                ></i>
                                À améliorer
                              </h5>
                              <p class="text-sm text-orange-700">
                                {{ answer.ai_aswer.pertinence_sujet.points_a_ameliorer }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Suggestions -->
                      <div
                        v-if="answer.ai_aswer.suggestions && answer.ai_aswer.suggestions.length > 0"
                        class="bg-white rounded-xl border-2 border-purple-100 shadow-sm"
                      >
                        <div
                          class="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-t-xl border-b border-purple-100"
                        >
                          <h4 class="font-bold text-purple-800 text-lg flex items-center">
                            <i class="fa-solid fa-lightbulb text-purple-500 mr-2"></i>
                            Suggestions d'amélioration
                          </h4>
                        </div>

                        <div class="p-4">
                          <div class="space-y-3">
                            <div
                              v-for="(suggestion, idx) in answer.ai_aswer.suggestions"
                              :key="idx"
                              class="flex items-start space-x-3 p-3 bg-purple-50 rounded-lg border border-purple-100"
                            >
                              <div
                                class="flex-shrink-0 w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center"
                              >
                                <span class="text-purple-700 font-bold text-xs">{{ idx + 1 }}</span>
                              </div>
                              <p class="text-purple-800 text-sm leading-relaxed">
                                {{ suggestion }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Suggestions -->
                <!--              <div v-if="answer.ai_aswer.suggestions && answer.ai_aswer.suggestions.length > 0" class="bg-white rounded-xl border-2 border-purple-100 shadow-sm">-->
                <!--                <div class="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-t-xl border-b border-purple-100">-->
                <!--                  <h4 class="font-bold text-purple-800 text-lg flex items-center">-->
                <!--                    <i class="fa-solid fa-lightbulb text-purple-500 mr-2"></i>-->
                <!--                    Suggestions d'amélioration-->
                <!--                  </h4>-->
                <!--                </div>-->

                <!--                <div class="p-4">-->
                <!--                  <div class="space-y-3">-->
                <!--                    <div v-for="(suggestion, idx) in answer.ai_aswer.suggestions" :key="idx"-->
                <!--                         class="flex items-start space-x-3 p-3 bg-purple-50 rounded-lg border border-purple-100">-->
                <!--                      <div class="flex-shrink-0 w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center">-->
                <!--                        <span class="text-purple-700 font-bold text-xs">{{ idx + 1 }}</span>-->
                <!--                      </div>-->
                <!--                      <p class="text-purple-800 text-sm leading-relaxed">{{ suggestion }}</p>-->
                <!--                    </div>-->
                <!--                  </div>-->
                <!--                </div>-->
                <!--              </div>-->
              </div>
            </div>
          </div>
        </div>

        <!-- Recommandations avec design moderne -->
        <div
          class="bg-white/90 mt-5 backdrop-blur-sm border-2 border-white/50 rounded-3xl p-8 shadow-2xl"
        >
          <h3
            class="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-8"
          >
            <i class="fa-solid fa-lightbulb text-yellow-500 mr-3"></i>
            Recommandations
          </h3>
          <div class="grid grid-cols-1 gap-6">
            <div
              v-for="recommendation in recommendations"
              :key="recommendation.type"
              class="group relative overflow-hidden p-6 rounded-2xl border-l-4 transition-all duration-300 transform hover:shadow-xl"
              :class="recommendation.class"
            >
              <div
                class="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full transform translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-500"
              ></div>
              <div class="relative z-10">
                <h4 class="font-bold text-lg mb-3">{{ recommendation.title }}</h4>
                <p class="text-gray-700 leading-relaxed">{{ recommendation.message }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useLanguageStore } from '@/stores/language'
import { useToast } from '@/composables/useToast'
import { useRouter } from 'vue-router'
import LoadingState from '@/components/exercices/LoadingState.vue'
import AudioPlayer from '@/components/common/audioPlayer.vue'
import moment from 'moment'

const props = defineProps({
  slug: {
    type: String,
    required: true,
  },
})

const store = useLanguageStore()
const { success, error: showError, info } = useToast()
const router = useRouter()

// Reactive state
const isLoading = ref(true)
const error = ref(null)
const filterCorrect = ref(true)
const filterIncorrect = ref(true)
const attemptData = ref(null)
const resultIsHovered = ref(null)
const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
const isLevelAttained = ref(false)
const currentLevel = ref(levels[0])
const correctingAnswer = ref([])

// Variables pour l'attente de correction
const isWaitingForCorrection = ref(false)
const correctionTimeout = ref(false)
const waitingTime = ref(0)
const correctionProgress = ref(0)
const totalExercises = ref(0)
const correctedExercises = ref(0)
const checkInterval = ref(null)
const timeoutTimer = ref(null)

// Computed
const currentExercice = computed(() => store.currentExercice)
const questions = computed(() => store.currentQuestions)
const userAnswers = computed(() => store.userAnswers)
const currentAttempt = computed(() => store.currentAttempt)
const ExerciceType = computed(() => attemptData.value?.exercice?.type_exam)

const finalScore = computed(() => {
  return attemptData.value?.score || 0
})

const correctAnswers = computed(() => {
  return userAnswers.value.filter((answer) => answerIsCorrect(answer)).length
})

const nbrAnswersText = computed(() => {
  return userAnswers.value.filter((answer) => answer?.user_aswer_text || answer?.answer_audio_path)
    .length
})

const incorrectAnswers = computed(() => {
  return userAnswers.value.filter((answer) => !answerIsCorrect(answer)).length
})

const correctPercentage = computed(() => {
  const total = userAnswers.value.length
  return total > 0 ? (correctAnswers.value / total) * 100 : 0
})

const timeSpent = computed(() => {
  return attemptData.value?.time_spent || 0
})

const questionTypeAnalysis = computed(() => {
  const analysis = {}

  questions.value.forEach((question) => {
    const type = question.question_type
    if (!analysis[type]) {
      analysis[type] = { correct: 0, total: 0, percentage: 0 }
    }
    analysis[type].total++

    const userAnswer = userAnswers.value.find((answer) => answer.questionSlug === question.slug)
    if (userAnswer && userAnswer.isCorrect === 1) {
      analysis[type].correct++
    }
  })

  // Calculate percentages
  Object.keys(analysis).forEach((type) => {
    const data = analysis[type]
    data.percentage = data.total > 0 ? (data.correct / data.total) * 100 : 0
  })

  return analysis
})

// Méthodes pour la gestion de l'attente
const startCorrectionWaiting = () => {
  isWaitingForCorrection.value = true
  correctionTimeout.value = false
  waitingTime.value = 0
  correctionProgress.value = 0

  // Calculer le nombre total d'exercices et corrigés initialement
  updateExerciseCount()

  // Timer pour le temps écoulé
  const timeInterval = setInterval(() => {
    waitingTime.value += 1
  }, 1000)

  // Vérification du statut toutes les 5 secondes
  checkInterval.value = setInterval(() => {
    checkCorrectionStatus()
  }, 5000)

  // Timeout après 5 minutes
  timeoutTimer.value = setTimeout(() => {
    handleCorrectionTimeout()
    clearInterval(timeInterval)
  }, 10000)
}

const updateExerciseCount = () => {
  if (attemptData.value?.exercice?.questions) {
    totalExercises.value = attemptData.value.exercice.questions.length
    correctedExercises.value = attemptData.value.exercice?.questions.filter(
      (answer) => answer.ai_aswer || (!answer.user_aswer_audio && !answer.user_aswer_text),
    ).length

    // Calculer le pourcentage de progression
    correctionProgress.value =
      totalExercises.value > 0 ? (correctedExercises.value / totalExercises.value) * 100 : 0
  }
}

const checkCorrectionStatus = async () => {
  try {
    const result = await store.fetchAttempStats(props.slug)

    if (result.exercice.type_exam === 'SPEAKING') {
      // Mettre à jour les compteurs
      const previousCorrected = correctedExercises.value
      updateExerciseCount()

      // Vérifier si tous les exercices sont corrigés
      const hasUncorrectedAnswers = result.exercice.questions?.some(
        (answer) => answer.user_aswer_audio && !answer.ai_aswer,
      )

      if (!hasUncorrectedAnswers) {
        completeCorrectionWaiting(result)
        return
      }

      // Mettre à jour les données si de nouveaux exercices ont été corrigés
      if (correctedExercises.value > previousCorrected) {
        attemptData.value = result
      }
    } else {
      completeCorrectionWaiting(result)
      return
    }
  } catch (err) {
    console.error('Error checking correction status:', err)
  }
}

const updateProgressAndStep = () => {
  const totalTime = correctionSteps.value.reduce((sum, step) => sum + step.duration, 0)
  const baseProgress = Math.min((waitingTime.value / totalTime) * 100, 95)

  const variance = (Math.random() - 0.5) * 2
  correctionProgress.value = Math.min(Math.max(baseProgress + variance, 0), 95)

  let elapsedTime = 0
  for (let i = 0; i < correctionSteps.value.length; i++) {
    elapsedTime += correctionSteps.value[i].duration
    if (waitingTime.value <= elapsedTime) {
      currentStep.value = i
      break
    }
  }

  if (currentStep.value >= correctionSteps.value.length) {
    currentStep.value = correctionSteps.value.length - 1
  }
}

const completeCorrectionWaiting = (result) => {
  // Compléter la progression à 100%
  correctionProgress.value = 100
  correctedExercises.value = totalExercises.value

  clearInterval(checkInterval.value)
  clearTimeout(timeoutTimer.value)

  attemptData.value = result

  setTimeout(() => {
    isWaitingForCorrection.value = false
    isLoading.value = false
  }, 1500)
}

const handleCorrectionTimeout = () => {
  clearInterval(checkInterval.value)

  isWaitingForCorrection.value = false
  correctionTimeout.value = true
  showError('Le délai de correction a été dépassé. Veuillez réessayer pour chaque réponse')
}

const resetCorrectionState = () => {
  clearInterval(checkInterval.value)
  clearTimeout(timeoutTimer.value)

  isWaitingForCorrection.value = false
  correctionTimeout.value = false
  waitingTime.value = 0
  correctionProgress.value = 0
  totalExercises.value = 0
  correctedExercises.value = 0
}

const isCorrectingAnsawer = (answer) => {
  return correctingAnswer.value.includes(answer)
}

const correctAnswer = async (answer) => {
  correctingAnswer.value.push(answer.slug)
  try {
    await store.correctAnswer(props.slug, answer.user_aswer_slug)
    success('Réponse corrigée avec succès.')
  } catch (e) {
    showError(
      'Erreur lors de la correction de la réponse. Veuillez vérifier votre connexion internet',
    )
  } finally {
    correctingAnswer.value = correctingAnswer.value.filter((a) => a !== answer.slug)
  }
}

const recommendations = computed(() => {
  const recs = []

  if (finalScore.value >= 400) {
    recs.push({
      type: 'excellent',
      title: 'Excellent travail !',
      message: "Vous maîtrisez bien ce type d'exercice. Essayez un niveau plus difficile.",
      class: 'border-green-500 bg-green-50',
    })
  } else if (finalScore.value >= 200) {
    recs.push({
      type: 'good',
      title: 'Bon résultat',
      message:
        'Vous êtes sur la bonne voie. Continuez à vous entraîner pour améliorer votre score.',
      class: 'border-yellow-500 bg-yellow-50',
    })
  } else {
    recs.push({
      type: 'improve',
      title: 'À améliorer',
      message: 'Reprenez les bases et refaites cet exercice. Ne vous découragez pas !',
      class: 'border-red-500 bg-red-50',
    })
  }

  // Analyse par type de question
  // Object.entries(questionTypeAnalysis.value).forEach(([type, data]) => {
  //   if (data.percentage < 50) {
  //     recs.push({
  //       type: 'weakness',
  //       title: `Travaillez les ${getQuestionTypeLabel(type).toLowerCase()}`,
  //       message: `Votre score de ${data.percentage.toFixed(0)}% indique qu'il faut renforcer ce type de question.`,
  //       class: 'border-orange-500 bg-orange-50'
  //     })
  //   }
  // })

  return recs
})

const filteredAnswers = computed(() => {
  return userAnswers.value.filter((answer) => {
    if (!filterCorrect.value && answerIsCorrect(answer)) return false
    if (!filterIncorrect.value && !answerIsCorrect(answer)) return false
    return true
  })
})

const attainedLevel = computed(() => {
  const score = finalScore.value

  if (score === undefined || score === null) {
    isLevelAttained.value = false
    return 'A1'
  }
  let level = 'A1'
  isLevelAttained.value = true
  if (score >= 600) {
    level = 'C2'
  } else if (score >= 500) {
    level = 'C1'
  } else if (score >= 400) {
    level = 'B2'
  } else if (score >= 300) {
    level = 'B1'
  } else if (score >= 200) {
    level = 'A2'
  } else if (score >= 50) {
    level = 'A1'
  } else {
    isLevelAttained.value = false
  }

  return level
})

const markerPosition = computed(() => {
  const levelIndex = levels.indexOf(attainedLevel.value)
  if (levelIndex === -1) {
    return '0%'
  }
  return `${(levelIndex / (levels.length - 1)) * 100}%`
})

// Methods
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

const getQuestionTitle = (question_text) => {
  try {
    const jsonObject = eval('(' + question_text + ')')
    return jsonObject?.titre || jsonObject?.sujet
  } catch (e) {
    return question_text
  }
}

const getScoreMessage = () => {
  if (finalScore.value >= 90) return 'Excellent !'
  if (finalScore.value >= 80) return 'Très bien !'
  if (finalScore.value >= 70) return 'Bien !'
  if (finalScore.value >= 60) return 'Assez bien'
  if (finalScore.value >= 50) return 'Passable'
  return 'À améliorer'
}

const getScoreColorClass = (score) => {
  if (score >= 80) return 'bg-green-500'
  if (score >= 60) return 'bg-yellow-500'
  return 'bg-red-500'
}

const getQuestionTypeLabel = (type) => {
  const labels = {
    MCQ: 'Choix multiples',
    TRUE_FALSE: 'Vrai/Faux',
    FILL_BLANK: 'Texte à trou',
    WRITTEN: 'Réponse écrite',
    SPEAKING: 'Expression orale',
  }
  return labels[type] || type
}

const getQuestionTypeClass = (type) => {
  const classes = {
    MCQ: 'bg-blue-100 text-blue-800',
    TRUE_FALSE: 'bg-green-100 text-green-800',
    FILL_BLANK: 'bg-purple-100 text-purple-800',
    WRITTEN: 'bg-orange-100 text-orange-800',
    SPEAKING: 'bg-pink-100 text-pink-800',
  }
  return classes[type] || 'bg-gray-100 text-gray-800'
}

const getQuestionFromSlug = (slug) => {
  return questions.value.find((q) => q.slug === slug)
}

const getChoiceText = (questionSlug, choiceId) => {
  const question = getQuestionFromSlug(questionSlug)
  if (!question || !question.choices) return 'Réponse inconnue'

  const choice = question.choices.find((c) => c.id === choiceId || c.slug === choiceId)
  return choice?.choice_text || 'Réponse inconnue'
}

const getCorrectAnswerText = (questionSlug) => {
  const question = getQuestionFromSlug(questionSlug)
  if (!question || !question.choices) return 'Réponse inconnue'

  const correctChoice = question.choices.find((c) => c.is_correct === 1)
  return correctChoice?.choice_text || 'Réponse inconnue'
}

const loadResults = async () => {
  try {
    isLoading.value = true
    error.value = null
    resetCorrectionState()

    attemptData.value = await store.fetchAttempStats(props.slug)

    if (attemptData.value?.exercice?.type_exam === 'SPEAKING') {
      const hasUncorrectedAnswers = attemptData.value.exercice.questions?.some(
        (answer) =>
          answer.user_aswer_audio &&
          !answer.ai_aswer &&
          !moment(attemptData.value.completed_at).isBefore(moment().subtract(10, 'minutes')),
      )

      if (hasUncorrectedAnswers) {
        isLoading.value = false
        startCorrectionWaiting()
        return
      }
    }

    isLoading.value = false
  } catch (err) {
    error.value = 'Erreur lors du chargement des résultats'
    showError(error.value)
    console.error(err)
    isLoading.value = false
  }
}

const retryExercice = async () => {
  try {
    isLoading.value = true
    const result = await store.startExercice(attemptData.value?.exercice?.slug)

    if (result.success) {
      success('Exercice démarré avec succès!')
      router.push({ name: 'exercice', params: { slug: attemptData.value?.exercice?.slug } })
    } else {
      showError(result.error || "Erreur lors du démarrage de l'exercice")
    }
  } catch (err) {
    showError("Erreur lors du démarrage de l'exercice")
    console.error(err)
  } finally {
    isLoading.value = false
  }
  // if (currentExercice.value) {
  //   const result = await store.startExercice(attemptData?.exercice?.slug)
  //   if (result.success) {
  //     router.push({ name: 'exercice', params: { slug: currentExercice.value.slug } })
  //   }
  // }
}

const goToExercices = () => {
  console.log(router.options.history.state.back)

  if (router.options.history.state.back.includes('/exercice/')) {
    store.resetCurrentExercice()
    router.go(-2)
    // router.push({ name: 'dashboard-exercises' })
  } else {
    router.back()
  }
}

const goToSimilarExercices = () => {
  router.push({ name: 'dashboard-exercises' })
}

const viewStats = () => {
  router.push({ name: 'dashboard' })
}

const answerIsCorrect = (answer) => {
  return (
    answer.user_choice[0]?.choice_text == answer.choices.find((c) => c.is_correct == 1)?.choice_text
  )
}
const getChoiceColor = (answer, idx_choice) => {
  //choisi et c'est vrai
  if (
    answer.user_choice[0]?.choice_text == answer.choices[idx_choice]?.choice_text &&
    answer.choices[idx_choice]?.is_correct
  )
    return 'bg-green-300 border-green-400'
  //choisi et c'est faux
  if (
    answer.user_choice[0]?.choice_text == answer.choices[idx_choice]?.choice_text &&
    !answer.choices[idx_choice]?.is_correct
  )
    return 'bg-red-300 border-red-400'
  // non choisi et c'est vrai
  if (
    answer.user_choice[0]?.choice_text != answer.choices[idx_choice]?.choice_text &&
    answer.choices[idx_choice]?.is_correct
  )
    return 'bg-green-300 border-green-500'
}

// Lifecycle
onMounted(() => {
  loadResults()
})
</script>

<style scoped>
.fa-books:before {
  content: '\f5db';
}
</style>
